import React, { useCallback } from 'react'

import { palette } from '@centivo/react-components'
import styled, { css } from 'styled-components'
import { useHistory } from 'react-router-dom'

import { PhoneNumber } from 'src/refactor/components/IconTextComponent'
import { IconDisplay } from 'src/refactor/blocks/IconDisplay'

import { ItemData } from 'src/context/results/types'
import { TPublishStatus } from 'src/shared/types'
import { useSliceTemplate } from 'src/context/global'
import {
  TPractitionerIdentifiers,
  generatePractitionerDetailsKey,
} from 'src/refactor/hooks/use-practitioner-details/usePractitionerDetails'
import { PreferredBadge } from 'src/refactor/components/PreferredBadge'
import { TResultListItemProps } from './types'
import {
  ParliPracticeName,
  ParliSpecialty,
} from './PractitionerResultListItem.styled'
import {
  RliAddress,
  RliContainer,
  RliDistance,
  RliTitle,
} from './ResultListItem.styled'

const Container = styled(RliContainer)<{ publish?: TPublishStatus }>`
  position: relative;

  ${({ theme }) => {
    switch (theme.template) {
      case 'template-4':
        return css`
          &:hover {
            background-color: #f7f4e6;
          }
        `
      default:
        return css`
          &:hover {
            background-color: #f7f4e6;
          }
        `
    }
  }}

  ${({ publish }) =>
    publish === 'N' &&
    css`
      margin-bottom: 1.5rem;

      &::after {
        content: 'Unpublished';
        position: absolute;
        background-color: ${palette.brand.newPurple};
        color: ${palette.neutral.white};
        border-radius: 0.125rem;
        font-weight: 600;
        font-size: 0.625rem;
        line-height: 1em;
        width: max-content;
        padding: 0.25rem;
        bottom: 1.25rem;
        right: 1.25rem;
      }
    `}
`

export const PractitionerResultListItem = React.forwardRef<
  HTMLDivElement,
  ItemData & TResultListItemProps
>(
  (
    {
      id,
      practiceName,
      distance,
      address1,
      address2,
      city,
      state,
      zipCode,
      fullAddress,
      fullName,
      phone,
      primarySpecialty,
      taxonomyDisplay,
      npi,
      network,
      lat,
      long,
      style,
      isVirtualCareOnly,
      hasVirtualCare,
      togglePopup,
      publish,
      isPreferred,
    },
    ref
  ) => {
    const history = useHistory()
    const goToDetails = useCallback(() => {
      const queryString = new URLSearchParams({
        ...generatePractitionerDetailsKey({
          npi,
          network,
          address1,
          address2,
          practiceName,
          city,
          state,
          zipCode,
          latitude: lat,
          longitude: long,
        } as any as TPractitionerIdentifiers),
        providerDistance: distance.toString(),
      }).toString()
      history.push(`/doctorDetails?${queryString}`)
    }, [distance, history, lat, long, network, npi, practiceName])
    const onHover = () => togglePopup(id)

    const isSliceTemplate = useSliceTemplate()

    return (
      <Container
        ref={ref}
        style={style}
        onClick={goToDetails}
        onMouseEnter={onHover}
        onMouseLeave={onHover}
        publish={publish}
        usePointerCursor
      >
        {isPreferred && <PreferredBadge />}
        <ParliPracticeName role="heading" aria-level={2}>
          {practiceName}
        </ParliPracticeName>
        <RliTitle gridRow="2">{fullName}</RliTitle>
        <ParliSpecialty>{taxonomyDisplay}</ParliSpecialty>

        {!isVirtualCareOnly && (
          <>
            <RliDistance>{distance} mi</RliDistance>

            <RliAddress>{address1}</RliAddress>
            <RliAddress>{fullAddress}</RliAddress>
          </>
        )}
        <div className="container__display">
          <PhoneNumber marginTop="0" number={phone as string} />

          {hasVirtualCare && (
            <IconDisplay
              marginTop="0"
              icon={isSliceTemplate ? 'slice-video' : 'video'}
            >
              {isVirtualCareOnly
                ? 'Virtual care only'
                : 'Virtual care available'}
            </IconDisplay>
          )}
        </div>
      </Container>
    )
  }
)
